import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Save,
  Analytics
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { assistantService, analysisPromptService, authService } from '../utils/api';
import DashboardLayout from './DashboardLayout';

function AnalysisPrompt() {
  const navigate = useNavigate();
  const { assistantId } = useParams();
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [promptContent, setPromptContent] = useState('');
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [hasPrompt, setHasPrompt] = useState(false);

  useEffect(() => {
    if (assistantId) {
      assistantService.getAssistantById(assistantId)
        .then(data => setSelectedAssistant(data))
        .catch(err => {
          toast.error(err.message || 'Failed to load assistant details.');
          navigate('/assistants');
        });
      setUserRole(authService.getUserRole());
      fetchAnalysisPrompt(assistantId);
    }
  }, [assistantId, navigate]);

  const fetchAnalysisPrompt = async (assistantId) => {
    try {
      const data = await analysisPromptService.getAnalysisPrompt(assistantId);
      setCurrentPrompt(data);
      setPromptContent(data.content);
      setHasPrompt(true);
    } catch (error) {
      if (error.detail === 'No analysis prompt found for this assistant') {
        setHasPrompt(false);
        setPromptContent('');
      } else {
        console.error('Error fetching analysis prompt:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrompt = async () => {
    if (!promptContent.trim()) {
      toast.error('Analysis prompt content cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const data = await analysisPromptService.createOrUpdateAnalysisPrompt(selectedAssistant.id, promptContent);
      setCurrentPrompt(data);
      setHasPrompt(true);
      toast.success(hasPrompt ? 'Analysis prompt updated successfully' : 'Analysis prompt created successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to save analysis prompt');
    } finally {
      setSaving(false);
    }
  };

  const isAdmin = userRole === 'super_admin' || userRole === 'admin';

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: 3 }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '1.25rem', mb: 0.5 }}>
              Analysis Prompt
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
              {selectedAssistant?.assistant_name || selectedAssistant?.name}
            </Typography>
          </Box>

          <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 3 }}>
              <Alert 
                severity="info" 
                icon={<Analytics />}
                sx={{ 
                  mb: 3, 
                  bgcolor: '#eff6ff', 
                  border: '1px solid #bfdbfe',
                  '& .MuiAlert-icon': { color: '#3b82f6' },
                  '& .MuiAlert-message': { color: '#1e40af', fontSize: '0.875rem' }
                }}
              >
                This prompt is used to analyze call transcripts. Define what information you want to extract from calls, such as sentiment, key topics, action items, or custom fields.
              </Alert>

              {currentPrompt && (
                <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #e2e8f0' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                    Last updated {new Date(currentPrompt.updated_at).toLocaleString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                rows={18}
                value={promptContent}
                onChange={(e) => setPromptContent(e.target.value)}
                placeholder="Enter your analysis prompt here... For example: Analyze this call transcript and extract the following information in JSON format: sentiment (positive/negative/neutral), key topics discussed, action items, and customer satisfaction score (1-10)."
                disabled={!isAdmin}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace',
                    fontSize: '0.8125rem',
                    lineHeight: 1.6,
                    bgcolor: '#f8fafc',
                    '& fieldset': {
                      borderColor: '#e2e8f0'
                    },
                    '&:hover fieldset': {
                      borderColor: '#cbd5e1'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: 1
                    }
                  },
                  '& .MuiInputBase-input': {
                    color: '#1e293b'
                  }
                }}
              />

              {isAdmin && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
                    onClick={handleSavePrompt}
                    disabled={saving || !promptContent.trim()}
                    sx={{ 
                      minWidth: 120,
                      height: 36,
                      bgcolor: '#3b82f6',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#2563eb',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              )}

              {!isAdmin && (
                <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic', fontSize: '0.75rem' }}>
                  View-only access. Contact an admin to edit.
                </Typography>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>
    </DashboardLayout>
  );
}

export default AnalysisPrompt;
